import React from 'react';
import PropTypes from 'prop-types';
import { Trans } from '@lingui/macro';
import { Collapse, LinearProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import VerifyResultContent from '../verify/VerifyResultContent';
import TestFormSection from './TestFormSection/TestFormSection';

const useStyles = makeStyles(() => ({
    verifyContent: {
        backgroundColor: '#EFEFEF',
        padding: 15,
        minHeight: 55,
        overflowX: 'auto',
        marginTop: 0,
    },
    verifyStatus: {
        fontWeight: 'bold',
    },
    statusMsg: {
        marginTop: 4,
    },
    loader: {
        marginBottom: 8,
    },
}));

/**
 * TestVerifyResultSection — displays verify test results.
 *
 * Matches portal/ui's verify.partial.html layout:
 * - FormSection with "Verify" title (like tvl-card label="Verify")
 * - Linear progress bar during loading (like md-progress-linear)
 * - Bold status text + status message + result content
 * - Gray background (#EFEFEF) content area
 *
 * Props are threaded from TestDetailPanel:
 * - showVerify: controls visibility via Collapse
 * - verifyLoading: shows progress bar during polling
 * - verifyResult: structured result object from ServiceTestVerifyHelper
 * - verifyResultSectionRef: verify Result Section Ref
 */
export default function TestVerifyResultSection({ showVerify, verifyLoading, verifyResult, verifyResultSectionRef }) {
    const classes = useStyles();

    return (
        <Collapse in={showVerify}>
            <TestFormSection title={<Trans>Verify</Trans>} addSpacer>
                <div id="verify-result-section" ref={verifyResultSectionRef} className={classes.verifyContent}>
                    {verifyLoading && <LinearProgress className={classes.loader} />}

                    {verifyResult && (
                        <>
                            <div className={classes.verifyStatus}>{verifyResult.status}</div>

                            {verifyResult.statusMsg && (
                                <div className={classes.statusMsg}>{verifyResult.statusMsg}</div>
                            )}

                            <VerifyResultContent verifyResult={verifyResult} />
                        </>
                    )}

                    {verifyLoading && !verifyResult && (
                        <div className={classes.verifyStatus}>
                            <Trans>Configuring nPoints to verify configuration...</Trans>
                        </div>
                    )}
                </div>
            </TestFormSection>
        </Collapse>
    );
}

TestVerifyResultSection.propTypes = {
    showVerify: PropTypes.bool,
    verifyLoading: PropTypes.bool,
    verifyResult: PropTypes.shape({
        status: PropTypes.string,
        statusMsg: PropTypes.string,
        resultType: PropTypes.string,
        resultData: PropTypes.object,
    }),
    verifyResultSectionRef: PropTypes.shape({
        current: PropTypes.object,
    }),
};

TestVerifyResultSection.defaultProps = {
    showVerify: false,
    verifyLoading: false,
    verifyResult: null,
    verifyResultSectionRef: null,
};
